import { Suspense } from 'react';
import { ErrorBoundary } from '@sentry/react';
import { Button, cn } from '@simplywallst/ui-core/next';
import { FairValueHistogram, CompanyNarrativesList } from './Narratives';
import { CompanyFeed } from '@/components/CompanyFeed';
import { toCurrencyTruncated } from '@/utilities/formatting';
import { useNarratives } from '../hooks/useNarratives';
import { MarketPerformance } from './MarketPerformance';
import { CompanySummary } from './CompanySummary';
import { CompanyDetails } from './CompanyDetails';
import { MarketInsights } from './MarketInsights';
import { useCompanyStock } from '../hooks/useCompanyStock';
import { PriceChart as DeprecatedPriceChart } from './DeprecatedPriceChart';
import { Competitors as CompanyCompetitors } from './Competitors';
import { ROUTE_NARRATIVE_COMMUNITY } from '@/constants/routes';
import { getPath } from '@/router/utils';
import { getCompanyUrlSegments } from '@/pages/Narrative/utils/getNarrativeUrl';
import { getReadableCountry } from '@/utilities/markets';
import { LoadingSpinner } from '@simplywallst/ui-core';
import type { PrimaryCountryISOElseGlobal } from '@/constants/countries';

const BackgroundGradient = ({
  isHidden = false,
  color,
}: {
  isHidden?: boolean;
  color: string;
}) => (
  <div
    className={cn(
      'pointer-events-none absolute left-0 top-0 aspect-1 w-3/4 select-none bg-[linear-gradient(140deg,var(--gradient-color)_0%,transparent_40%)] opacity-[.04] transition-opacity delay-700 duration-1000 ease-out',
      isHidden && 'opacity-0'
    )}
    style={{ '--gradient-color': color } as React.CSSProperties}
  />
);

const Section = ({
  children,
  className,
  isPadded,
}: {
  children: React.ReactNode;
  className?: string;
  isPadded?: boolean;
}) => (
  <section>
    <div
      className={cn(
        'flex flex-col gap-3.5 rounded-lg bg-surface-1 shadow-sm',
        isPadded ? 'p-5' : 'py-5',
        className
      )}
    >
      {children}
    </div>
  </section>
);

Section.Padding = function Padding({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'gap-inherit px-5 [display:inherit] [flex-direction:inherit]',
        className
      )}
    >
      {children}
    </div>
  );
};

const SubHeading = ({
  children,
  className,
  isSmall = false,
}: {
  children: React.ReactNode;
  className?: string;
  isSmall?: boolean;
}) => (
  <h2
    className={cn(
      'm-0 text-pretty font-medium [text-box-trim:trim-both]',
      isSmall ? 'text-base lg:text-sm' : 'text-base',
      className
    )}
  >
    {children}
  </h2>
);

const splitCurrencyAtFirstNumber = (currency: string) => {
  // Match from the first digit (optionally negative, optionally decimal) to the end
  const match = currency.match(/[-]?\d*\.?\d+$/);
  if (!match) return currency;
  return match[0];
};

export const Chart = () => {
  const company = useCompanyStock();

  if (!company.canonicalUrl) return;

  return (
    <Section className="relative overflow-hidden bg-surface-2 pb-0" isPadded>
      <SubHeading>{company.tickerSymbol} Share Price Performance</SubHeading>
      <DeprecatedPriceChart
        lookup={company.canonicalUrl}
        currencyISO={company.listingCurrencyISO ?? 'USD'}
      >
        {(chartData) => {
          const data = {
            lastPrice:
              company.listingCurrencyISO &&
              toCurrencyTruncated(
                chartData.lastPrice ?? 0,
                2,
                company.listingCurrencyISO
              ),
            priceChange:
              company.listingCurrencyISO &&
              splitCurrencyAtFirstNumber(
                toCurrencyTruncated(
                  chartData.priceChange,
                  2,
                  company.listingCurrencyISO
                )
              ),
            percentageChange:
              ((chartData.lastPrice - chartData.firstPrice) /
                chartData.firstPrice) *
              100,
          };
          return (
            <div>
              {data.lastPrice && (
                <>
                  <span className="text-2xl">{data.lastPrice}</span>
                  {' '}
                </>
              )}
              {data.priceChange && (
                <span className="text-sm text-[--chart-line-color]">
                  {chartData.isPositiveTrend ? '↑' : '↓'}
                  {data.priceChange}
                </span>
              )}
              {' '}
              <span className="text-sm text-[--chart-line-color]">
                ({data.percentageChange.toFixed(1)}%)
              </span>
              <BackgroundGradient
                isHidden={chartData.isLoading}
                color="var(--chart-line-color)"
              />
            </div>
          );
        }}
      </DeprecatedPriceChart>
    </Section>
  );
};

const NarrativesContent = ({
  hasSlider = true,
  canonicalUrl,
}: {
  hasSlider?: boolean;
  canonicalUrl: string;
}) => {
  const narratives = useNarratives({ canonicalUrl });

  return (
    <>
      {narratives.shouldShowHistogram && (
        <Section.Padding>
          <FairValueHistogram company={narratives.company} height={270} />
        </Section.Padding>
      )}
      {hasSlider ? (
        <CompanyNarrativesList
          narratives={narratives.narratives}
          classNameList="[&>li]:px-5 [&>li:not(:first-child)]:-ml-10"
        />
      ) : (
        <Section.Padding>
          <CompanyNarrativesList
            narratives={narratives.narratives}
            hasSlider={false}
            classNameList="sm:grid-cols-[repeat(auto-fill,minmax(305px,1fr))]"
          />
        </Section.Padding>
      )}
    </>
  );
};

export const Narratives = ({ hasSlider = true }: { hasSlider?: boolean }) => {
  const company = useCompanyStock();

  if (!company.canonicalUrl) return;

  // TODO: Fix this, constructing the community url from the company profile url is a bit silly
  const { country, industry, exchangeTicker, companyName } =
    getCompanyUrlSegments(company.canonicalUrl);

  // This avoids showing a confusing header when the ticker is a chinese numbered one
  const symbolIsNumber = company.tickerSymbol?.match(/^\d+$/);

  return (
    <Section className="relative overflow-hidden">
      <Section.Padding>
        <SubHeading>
          {symbolIsNumber
            ? 'Community Fair Values'
            : `${company.tickerSymbol} Community Fair Values`}
        </SubHeading>
      </Section.Padding>
      {/*
       * This ErrorBoundary hides some "on-load" errors - the suspense hooks
       * have incorrect return types and null data isn't reported while fetching.
       */}
      <ErrorBoundary>
        <Suspense
          fallback={
            <div className="grid min-h-[300px] place-items-center">
              <LoadingSpinner light />
            </div>
          }
        >
          <NarrativesContent
            hasSlider={hasSlider}
            canonicalUrl={company.canonicalUrl}
          />
        </Suspense>
      </ErrorBoundary>
      <Section.Padding>
        <div>
          <Button
            href={getPath({
              type: ROUTE_NARRATIVE_COMMUNITY,
              payload: {
                country,
                industry,
                exchange_ticker: exchangeTicker,
                companyname: companyName,
              },
            }).toLowerCase()}
            variant="secondary"
          >
            View all narratives
          </Button>
        </div>
      </Section.Padding>
      <BackgroundGradient color="var(--s-color-chart-03)" />
    </Section>
  );
};

export const News = () => {
  const company = useCompanyStock();

  return (
    <Section className="relative overflow-hidden" isPadded>
      <SubHeading>Recent {company.tickerSymbol} News &amp; Updates</SubHeading>
      <div className="@container">
        <div className="grid items-start gap-x-3 gap-y-1 @[100ch]:grid-cols-2 [&_article]:-ml-2 [&_article_h4]:text-pretty">
          <CompanyFeed
            lookup={company.canonicalUrl}
            fetchLimit={14}
            showLimit={14}
            titleElement="h3"
            isCompanyReport
          />
        </div>
      </div>
      <BackgroundGradient color="#FF9212" />
    </Section>
  );
};

export const Summary = () => {
  const company = useCompanyStock();
  return (
    <Section isPadded>
      <SubHeading isSmall className="line-clamp-2">
        About {company.tickerSymbol}
      </SubHeading>
      <Suspense
        fallback={
          <div className="grid min-h-[207px] place-items-center">
            <LoadingSpinner light />
          </div>
        }
      >
        <ErrorBoundary>
          <CompanySummary company={company} />
        </ErrorBoundary>
      </Suspense>
    </Section>
  );
};

export const Details = () => {
  const company = useCompanyStock();

  return (
    <Section isPadded>
      <SubHeading isSmall className="line-clamp-2">
        {company.name} Key Details
      </SubHeading>
      <Suspense
        fallback={
          <div className="grid min-h-[207px] place-items-center">
            <LoadingSpinner light />
          </div>
        }
      >
        <ErrorBoundary>
          <CompanyDetails
            canonicalUrl={company.canonicalUrl}
            details={company.details}
          />
        </ErrorBoundary>
      </Suspense>
    </Section>
  );
};

export const Performance = () => {
  const company = useCompanyStock();

  if (!company.countryISO) return;

  return (
    <Section isPadded>
      <SubHeading isSmall>
        {getReadableCountry(
          company.countryISO.toLowerCase() as PrimaryCountryISOElseGlobal
        )}{' '}
        Market Performance
      </SubHeading>
      <MarketPerformance market={company.countryISO} industryId={0} />
    </Section>
  );
};

export const Competitors = () => {
  const company = useCompanyStock();

  if (!company.countryISO) return;

  return (
    <Section isPadded>
      <SubHeading isSmall>{company.name} Competitors</SubHeading>
      <CompanyCompetitors />
    </Section>
  );
};

export const Insights = () => {
  return (
    <section className="lg:mb-8">
      <MarketInsights />
    </section>
  );
};
